import {Controller} from "@hotwired/stimulus"

// Connects to data-controller="checkout-address-autocomplete"
export default class extends Controller {
    static targets = [
        "addressInput", "address2Input", "cityInput", "stateInput", "zipcodeInput",
        "billAddressInput", "billAddress2Input", "billCityInput", "billStateInput", "billZipcodeInput"
    ]
    static values = {
        apiKey: String,
        fieldPrefix: {type: String, default: "order"}
    }

    connect() {
        //console.log('Checkout Address Autocomplete controller connected');
        this.loadGoogleMapsApi()
            .then(() => {
                this.initializeAutocomplete();
                this.initializeBillingAutocomplete();
            })
            .catch(error => {
                console.error('Error loading Google Maps API:', error);
            });
    }

    /**
     * Load Google Maps API script if not already loaded
     */
    loadGoogleMapsApi() {
        return new Promise((resolve, reject) => {
            // If already loaded, resolve immediately
            if (typeof google !== 'undefined' && google.maps && google.maps.places) {
                resolve();
                return;
            }

            // Check if script is already being loaded
            const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
            if (existingScript) {
                existingScript.addEventListener('load', resolve);
                existingScript.addEventListener('error', reject);
                return;
            }

            // Create script element
            const script = document.createElement('script');
            script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKeyValue}&libraries=places&v=weekly`;
            script.async = true;
            script.defer = true;

            // Setup load and error handlers
            script.onload = () => {
                //console.log('Google Maps API loaded successfully');
                resolve();
            };
            script.onerror = () => {
                console.error('Failed to load Google Maps API');
                reject(new Error('Failed to load Google Maps API'));
            };

            // Add script to document
            document.head.appendChild(script);
        });
    }

    /**
     * Initialize Google Places autocomplete on the address input
     */
    initializeAutocomplete() {
        if (!this.hasAddressInputTarget) {
            console.warn('Address input target not found');
            return;
        }

        // Make sure Google Maps API is loaded
        if (typeof google === 'undefined' || !google.maps || !google.maps.places) {
            console.warn('Google Maps Places API not loaded');
            return;
        }

        try {
            // Create the autocomplete instance
            this.autocomplete = new google.maps.places.Autocomplete(this.addressInputTarget, {
                componentRestrictions: {country: ["us", "ca"]},
                fields: ["address_components", "geometry"],
                types: ["address"]
            });

            // Listen for place selection
            this.autocomplete.addListener('place_changed', () => {
                this.handlePlaceChanged();
            });

            //console.log('Google Places autocomplete initialized successfully');
        } catch (error) {
            console.error('Error initializing Google Places autocomplete:', error);
        }
    }

    /**
     * Initialize Google Places autocomplete on the billing address input
     */
    initializeBillingAutocomplete() {
        if (!this.hasBillAddressInputTarget) {
            console.warn('Billing address input target not found');
            return;
        }

        // Make sure Google Maps API is loaded
        if (typeof google === 'undefined' || !google.maps || !google.maps.places) {
            console.warn('Google Maps Places API not loaded');
            return;
        }

        try {
            // Create the autocomplete instance for billing
            this.billingAutocomplete = new google.maps.places.Autocomplete(this.billAddressInputTarget, {
                componentRestrictions: {country: ["us", "ca"]},
                fields: ["address_components", "geometry"],
                types: ["address"]
            });

            // Listen for place selection
            this.billingAutocomplete.addListener('place_changed', () => {
                this.handleBillingPlaceChanged();
            });

            //console.log('Google Places billing autocomplete initialized successfully');
        } catch (error) {
            console.error('Error initializing Google Places billing autocomplete:', error);
        }
    }

    /**
     * Handle when a place is selected from autocomplete (Shipping)
     */
    handlePlaceChanged() {
        const place = this.autocomplete.getPlace();

        if (!place || !place.address_components) {
            console.warn('No place data available');
            return;
        }

        let address1 = "";
        let postcode = "";

        // Process each address component
        place.address_components.forEach((component) => {
            const componentType = component.types[0];

            switch (componentType) {
                case "street_number":
                    address1 = `${component.long_name} ${address1}`;
                    break;

                case "route":
                    address1 += component.short_name;
                    break;

                case "postal_code":
                    postcode = `${component.long_name}${postcode}`;
                    break;

                case "postal_code_suffix":
                    postcode = `${postcode}-${component.long_name}`;
                    break;

                case "locality":
                    if (this.hasCityInputTarget) {
                        this.cityInputTarget.value = component.long_name;
                    }
                    break;

                case "administrative_area_level_1":
                    this.updateStateField(component.short_name);
                    break;
            }
        });

        // Update address1 field
        if (this.hasAddressInputTarget) {
            this.addressInputTarget.value = address1.trim();
        }

        // Update zipcode field
        if (this.hasZipcodeInputTarget) {
            this.zipcodeInputTarget.value = postcode.trim();
        }

        // Clear validation errors from all populated fields
        this.clearValidationErrors([
            this.addressInputTarget,
            this.cityInputTarget,
            this.stateInputTarget,
            this.zipcodeInputTarget
        ]);

        // Revalidate fields with FormValidation if available
        this.revalidateFields([
            'order[ship_address1]',
            'order[ship_city]',
            'order[ship_state]',
            'order[ship_zipcode]'
        ]);

        // Focus on address2 field for user to continue
        if (this.hasAddress2InputTarget) {
            this.address2InputTarget.focus();
        }
    }

    /**
     * Handle when a place is selected from billing autocomplete
     */
    handleBillingPlaceChanged() {
        const place = this.billingAutocomplete.getPlace();

        if (!place || !place.address_components) {
            console.warn('No place data available for billing');
            return;
        }

        let address1 = "";
        let postcode = "";

        // Process each address component
        place.address_components.forEach((component) => {
            const componentType = component.types[0];

            switch (componentType) {
                case "street_number":
                    address1 = `${component.long_name} ${address1}`;
                    break;

                case "route":
                    address1 += component.short_name;
                    break;

                case "postal_code":
                    postcode = `${component.long_name}${postcode}`;
                    break;

                case "postal_code_suffix":
                    postcode = `${postcode}-${component.long_name}`;
                    break;

                case "locality":
                    if (this.hasBillCityInputTarget) {
                        this.billCityInputTarget.value = component.long_name;
                    }
                    break;

                case "administrative_area_level_1":
                    this.updateBillingStateField(component.short_name);
                    break;
            }
        });

        // Update billing address1 field
        if (this.hasBillAddressInputTarget) {
            this.billAddressInputTarget.value = address1.trim();
        }

        // Update billing zipcode field
        if (this.hasBillZipcodeInputTarget) {
            this.billZipcodeInputTarget.value = postcode.trim();
        }

        // Clear validation errors from all populated billing fields
        this.clearValidationErrors([
            this.billAddressInputTarget,
            this.billCityInputTarget,
            this.billStateInputTarget,
            this.billZipcodeInputTarget
        ]);

        // Revalidate billing fields with FormValidation if available
        this.revalidateFields([
            'order[bill_address1]',
            'order[bill_city]',
            'order[bill_state]',
            'order[bill_zipcode]'
        ]);

        // Focus on billing address2 field for user to continue
        if (this.hasBillAddress2InputTarget) {
            this.billAddress2InputTarget.focus();
        }
    }

    /**
     * Update the state field (handles both select and text input)
     */
    updateStateField(stateCode) {
        if (!this.hasStateInputTarget) return;

        const stateField = this.stateInputTarget;

        if (stateField.tagName === 'SELECT') {
            // It's a select dropdown - set the value
            stateField.value = stateCode;

            // Trigger change event in case there are listeners
            stateField.dispatchEvent(new Event('change', {bubbles: true}));
        } else {
            // It's a text input
            stateField.value = stateCode;
        }
    }

    /**
     * Update the billing state field (handles both select and text input)
     */
    updateBillingStateField(stateCode) {
        if (!this.hasBillStateInputTarget) return;

        const stateField = this.billStateInputTarget;

        if (stateField.tagName === 'SELECT') {
            // It's a select dropdown - set the value
            stateField.value = stateCode;

            // Trigger change event in case there are listeners
            stateField.dispatchEvent(new Event('change', {bubbles: true}));
        } else {
            // It's a text input
            stateField.value = stateCode;
        }
    }

    /**
     * Clear validation error classes from fields
     */
    clearValidationErrors(fields) {
        fields.forEach(field => {
            if (field) {
                field.classList.remove('is-invalid');
                
                // Also remove FormValidation error messages
                const parent = field.closest('.fv-row');
                if (parent) {
                    const feedback = parent.querySelector('.invalid-feedback');
                    if (feedback) {
                        feedback.style.display = 'none';
                    }
                }
            }
        });
    }

    /**
     * Revalidate fields with FormValidation if available
     */
    revalidateFields(fieldNames) {
        // Find the form element
        const form = this.element.closest('form');
        if (!form || !form._formValidation) {
            return;
        }

        const formValidation = form._formValidation;

        // Revalidate each field
        fieldNames.forEach(fieldName => {
            // Use setTimeout to allow the value to be set first
            setTimeout(() => {
                formValidation.validateField(fieldName);
            }, 50);
        });
    }

    disconnect() {
        if (this.autocomplete) {
            // Clean up Google Maps autocomplete listeners
            google.maps.event.clearInstanceListeners(this.autocomplete);
        }
        if (this.billingAutocomplete) {
            // Clean up billing autocomplete listeners
            google.maps.event.clearInstanceListeners(this.billingAutocomplete);
        }
    }
}
